import { provideState } from '@ngrx/store';

import {
  createEntityCollectionStore,
  EntityState,
  getEntityCollectionActions,
} from '@core/ngrx-legacy';
import {
  TellentNavigationHttpContract,
  TellentNavigationViewID,
  TellentNavigationScope,
  TellentNavigationLinkLocalState,
  TellentNavigationItem,
  TellentNavigationLink,
} from '@core/tellent-types';

const { actions, selectors, reducer, namespace } =
  createEntityCollectionStore<TellentNavigationHttpContract>()('tellent-navigation', {
    customActions: adapter => {
      const reducerFunctions = getEntityCollectionActions(
        adapter,
        ({ id }: { id: TellentNavigationViewID }) => id,
      );

      return {
        fetchMainNav: (state, payload: { navScope: TellentNavigationScope[] }) =>
          reducerFunctions.fetchCollection(state, {
            scope: TellentNavigationViewID.Main,
            ...payload,
          }),
        fetchMainNavSuccess: (state, payload: { data: TellentNavigationHttpContract[] }) =>
          reducerFunctions.fetchCollectionSuccess(state, {
            scope: TellentNavigationViewID.Main,
            data: applyLocalState(payload.data, {
              ...(state.entityMap[TellentNavigationViewID.Main]?.linksLocalState ?? {}),
            }),
          }),
        fetchMainNavFailed: (state, payload) =>
          reducerFunctions.fetchCollectionFailed(state, {
            scope: TellentNavigationViewID.Main,
            ...payload,
          }),
        fetchSettingsNav: (state, payload: { navScope: TellentNavigationScope[] }) =>
          reducerFunctions.fetchCollection(state, {
            scope: TellentNavigationViewID.Settings,
            ...payload,
          }),
        fetchSettingsNavSuccess: (state, payload: { data: TellentNavigationHttpContract[] }) =>
          reducerFunctions.fetchCollectionSuccess(state, {
            scope: TellentNavigationViewID.Settings,
            data: applyLocalState(payload.data, {
              ...(state.entityMap[TellentNavigationViewID.Settings]?.linksLocalState ?? {}),
            }),
          }),
        fetchSettingsNavFailed: (state, payload) =>
          reducerFunctions.fetchCollectionFailed(state, {
            scope: TellentNavigationViewID.Settings,
            ...payload,
          }),
        patchNavigationLink: (
          state,
          payload: TellentNavigationLinkLocalState & {
            scope: `${TellentNavigationViewID}`;
            patchName: string;
          },
        ) => patchNavigationLinkActionReducer(state, payload),
      };
    },
  });

const provider = provideState(namespace, reducer);

const TellentNavigationActions = {
  fetchMainNav: actions.fetchMainNav,
  fetchMainNavSuccess: actions.fetchMainNavSuccess,
  fetchMainNavFailed: actions.fetchMainNavFailed,

  fetchSettingsNav: actions.fetchSettingsNav,
  fetchSettingsNavSuccess: actions.fetchSettingsNavSuccess,
  fetchSettingsNavFailed: actions.fetchSettingsNavFailed,

  patchNavigationItem: actions.patchNavigationLink,
};

const TellentNavigationSelectors = {
  selectMainNav: selectors.byId(TellentNavigationViewID.Main),
  selectMainNavMeta: selectors.meta({ scope: TellentNavigationViewID.Main }),

  selectSettingsNav: selectors.byId(TellentNavigationViewID.Settings),
  selectSettingsNavMeta: selectors.meta({ scope: TellentNavigationViewID.Settings }),
};

const patchNavigationLinkActionReducer = (
  state: EntityState<TellentNavigationHttpContract>,
  payload: TellentNavigationLinkLocalState & {
    scope: `${TellentNavigationViewID}`;
    patchName: string;
  },
) => {
  const { target, patch, patchName, scope } = payload;

  const linksLocalState = {
    ...(state.entityMap[scope]?.linksLocalState ?? {}),
    [patchName]: { target, patch },
  };

  const [data] = applyLocalState(
    state.entityMap[scope] ? [state.entityMap[scope]] : [],
    linksLocalState,
  );

  return data
    ? {
        ...state,
        entityMap: {
          ...(state.entityMap || {}),
          [scope]: {
            ...data,
            linksLocalState,
          },
        },
      }
    : {
        ...state,
        entityMap: <any>{
          ...(state.entityMap || {}),
          [scope]: {
            ...(state.entityMap || {}),
            linksLocalState,
          },
        },
      };
};

const updateItemsWithLocalState = (
  items: TellentNavigationItem[],
  linksLocalState: Record<string, TellentNavigationLinkLocalState>,
): TellentNavigationItem[] =>
  items.map(item => {
    if (item.type === 'link') {
      // Find matching patch for this link
      const matchingPatch = Object.values(linksLocalState).find(
        patch => patch.target.page.id === item.page.id,
      );

      if (matchingPatch) {
        return <TellentNavigationLink>{ ...item, ...matchingPatch.patch };
      }

      return item;
    }

    if (item.type === 'group' || item.type === 'section') {
      return {
        ...item,
        children: updateItemsWithLocalState(item.children, linksLocalState),
      };
    }

    return item;
  });

const applyLocalState = (
  data: TellentNavigationHttpContract[],
  linksLocalState: Record<string, TellentNavigationLinkLocalState>,
): TellentNavigationHttpContract[] =>
  data.map(_data => ({
    ..._data,
    sections: updateItemsWithLocalState(_data.sections, linksLocalState),
    linksLocalState,
  }));

export {
  TellentNavigationActions,
  TellentNavigationSelectors,
  provider as TellentNavigationStateProvider,
  namespace as TellentNavigationNamespace,
  reducer as TellentNavigationReducer,
};
